import type { DiscountFigures } from "~/components/demand-2/demand/DiscountsBreakdown"
import MarkdownVisibilityNote from "~/components/demand-2/demand/MarkdownVisibilityNote"
import { signedPct, signedUnits } from "~/components/demand-2/shared/demandBreakdownShared"
import { AnswerPicker } from "~/components/demand-2/shared/FramingAnswerPicker"
import { useAllSlicesGuard } from "~/components/demand-2/shared/useAllSlicesGuard"
import { useScopedFramingAnswer } from "~/components/demand-2/shared/useScopedFramingAnswer"
import {
  isAllSlice,
  scopeNode,
  type AnswerScope,
  type FramingDescribeContext,
  type FramingNode,
  WHOLE_COLLECTION_NODE,
  WHOLE_SCOPE,
} from "~/utils/demandFraming"

/** The `discounts` question, via the same picker and store as the season rate and Product mix. */
const DiscountsAnswer = ({
  node: nodeProp = WHOLE_COLLECTION_NODE,
  scope: scopeProp,
  figures,
}: {
  /** Falls back to the collection's answer. */
  node?: FramingNode
  /** Overrides `node`, e.g. one variant from the Total page. */
  scope?: AnswerScope
  figures: DiscountFigures
}) => {
  const read = useScopedFramingAnswer("discounts", nodeProp, scopeProp)
  const guard = useAllSlicesGuard({
    question: "discounts",
    collectionId: read?.collectionId ?? 0,
    scope: read?.scope ?? WHOLE_SCOPE,
  })
  if (read == null) return null
  const { collectionId, kind, scope, answer, inherited, isDefault } = read
  // NOOS measures on exactly what the baseline reads (own sales or matches), not on comparables.
  const ownSales = kind === "noos"
  const node = scopeNode(scope)
  const isCollection = scope.kind === "whole" && isAllSlice(scope.slice)
  const context: FramingDescribeContext = {
    discounts: {
      nodeName: node.groupKey === "" ? undefined : node.groupValue,
      ownSales,
      comparableSales: figures.comparableSales,
      discountedSales: figures.discountedSales,
      impactUnits: figures.impactUnits,
    },
  }
  // So the line under an option and the figure after picking it agree.
  const impactOf = (optionId: string): React.ReactNode => {
    if (figures.comparableSales <= 0) return null
    if (optionId === "maintain") {
      return <span className="paragraph-sm text-fg-subtle">No change to the plan</span>
    }
    return (
      <span className="paragraph-sm text-fg-subtle">
        <span className="text-fg-error-icon">
          {signedUnits(figures.impactUnits)} units ·{" "}
          {signedPct(figures.impactUnits / figures.comparableSales)}
        </span>{" "}
        {ownSales ? "of the baseline reorders" : "of the comparable reorders"}
      </span>
    )
  }
  return (
    <>
      <AnswerPicker
        collectionId={collectionId}
        question="discounts"
        answer={answer}
        scope={scope}
        inherited={inherited}
        isDefault={isDefault}
        describeContext={context}
        onWrite={guard.write}
        pending={guard.pending}
        optionFooter={impactOf}
        underOption={
          isCollection ? (optionId) => <MarkdownVisibilityNote optionId={optionId} /> : undefined
        }
      />
      {guard.dialog}
    </>
  )
}

export default DiscountsAnswer
